import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { Deal } from "@/lib/types/database/deals";
import {
  coreDealsFields,
  financialDealsFields,
  companyDealsFields,
  metadataDealsFields,
  timelineDealsFields,
  contactDealsFields,
  projectHealthDealsFields,
  dealActions,
  dealBulkActions,
  dealSavedViews,
  dealFeatures,
} from "../config";
import { ENHANCED_DEAL_DETAIL_CONFIG } from "./DealDetailConfig";

/** Logical order for create deal form: identity → company → stage/value → dates → financials → people → health → notes/tags */
const CREATE_DEAL_FIELD_ORDER: string[] = [
  "title",
  "company_id",
  "stage",
  "revenue",
  "close_date",
  "close_probability",
  "won_at",
  "lost_at",
  "is_recurring",
  "monthly_amount",
  "is_committed",
  "cost",
  "estimated_hours",
  "assigned_users",
  "engineer_ids",
  "schedule_status",
  "budget_status",
  "scope_status",
  "alignment_status",
  "description",
  "notes",
  "tags",
];

/**
 * Progressive-disclosure section layout for the Create Deal form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_DEAL_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  // Essentials — what a salesperson needs to log a deal on the go: deal, account,
  // stage, value. All four are zod-required, so this card is always visible.
  // `revenue` lives here (not under Financials) so the deal value is captured up
  // front and the Financials group can genuinely collapse on mobile.
  {
    id: "core",
    kind: "always",
    fieldNames: ["title", "company_id", "stage", "revenue"],
  },
  // Optional groups: full cards on desktop, collapsed accordions on a phone.
  {
    id: "classification",
    kind: "collapsible",
    collapseOnMobileOnly: true,
    summary: "Probability, owner, engineers, tags",
    fieldNames: [
      "close_probability",
      "assigned_users",
      "engineer_ids",
      "tags",
    ],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    collapseOnMobileOnly: true,
    title: "Pipeline timeline",
    summary: "Close date, won/lost timestamps",
    fieldNames: ["close_date", "won_at", "lost_at"],
  },
  {
    id: "financials",
    kind: "collapsible",
    collapseOnMobileOnly: true,
    title: "Financials",
    summary: "Cost, recurring, estimated hours",
    fieldNames: [
      "cost",
      "is_recurring",
      "monthly_amount",
      "is_committed",
      "estimated_hours",
    ],
  },
  {
    id: "project-health",
    kind: "collapsible",
    collapseOnMobileOnly: true,
    title: "Project health",
    summary: "Schedule, budget, scope, alignment",
    fieldNames: [
      "schedule_status",
      "budget_status",
      "scope_status",
      "alignment_status",
    ],
  },
  {
    id: "description",
    kind: "collapsible",
    collapseOnMobileOnly: true,
    title: "Description & notes",
    fieldNames: ["description", "notes"],
  },
];

export const ENHANCED_DEALS_CONFIG: SimpleTableConfig<Deal> = {
  entity: "deals",
  displayName: "Deals",
  statusFieldId: "stage",
  defaultPageSize: 10,
  boardViewPageSize: 250,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,

  mobileDetailSections: [
    { id: "overview", label: "Overview" },
    { id: "company", label: "Company" },
    { id: "financial", label: "Financial" },
    { id: "people", label: "People" },
    { id: "details", label: "Description & Notes" },
    { id: "timeline", label: "Timeline" },
    { id: "project_health", label: "Project Health" },
  ],

  fields: {
    ...coreDealsFields,
    ...companyDealsFields,
    ...financialDealsFields,
    ...contactDealsFields,
    ...metadataDealsFields,
    ...timelineDealsFields,
    ...projectHealthDealsFields,
  },

  createFormFieldOrder: CREATE_DEAL_FIELD_ORDER,
  createFormSections: CREATE_DEAL_FORM_SECTIONS,

  /** Default table column order: deal → account → pipeline stage/value → dates → owner */
  tableColumnOrder: [
    "title",
    "company_name",
    "stage",
    "revenue",
    "close_date",
    "close_probability",
    "contract_status",
    "assigned_users",
  ],

  /** Card view: identity → pipeline metrics (dense row) → account → people → tags */
  cardFaceFieldOrder: [
    "title",
    "stage",
    "revenue",
    "close_date",
    "close_probability",
    "company_name",
    "assigned_users",
    "engineer_ids",
    "tags",
  ],

  actions: dealActions,

  // Add detail page actions for table rows - reuse from detail config (like companies)
  detailActions: ENHANCED_DEAL_DETAIL_CONFIG.actions,

  bulkActions: dealBulkActions,

  features: dealFeatures,

  savedViews: dealSavedViews,

  // Custom filter hooks for saved views that require special filtering logic
  customFilterHooks: {},
};
